import { motion } from "framer-motion";
import { useState, useEffect } from "react";

export default function ProductsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById("products");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/portfolio.pdf";
    link.download = "PrimeEdge-Portfolio.pdf";
    link.click();
  };
  const categories = [
    { id: "all", name: "All Products", icon: "ri-grid-line" },
    { id: "hardware", name: "Hardware", icon: "ri-table-line" },
    { id: "furniture", name: "Furniture", icon: "ri-armchair-line" },
    { id: "doors", name: "Doors", icon: "ri-archive-line" },
    { id: "handrail", name: "Handrail", icon: "ri-group-line" },
    { id: "others", name: "Others", icon: "ri-group-line" },
  ];

  const products = [
    {
      id: 1,
      name: "Executive Pro Desk",
      category: "furniture",

      image: "furniture/executiveDesk.png",
    },
    {
      id: 2,
      name: "Chairs & Sofas",
      category: "furniture",

      image: "furniture/chairs-sofas.png",
    },
    {
      id: 3,
      name: "Work Station",
      category: "furniture",

      image: "furniture/work-station.png",
    },
    {
      id: 4,
      name: "Storage Cabinet & Shelfs",
      category: "furniture",

      image: "furniture/shelfs.png",
    },
    {
      id: 5,
      name: "Steel Doors",
      category: "doors",
      image: "Doors/steel-ddor.png",
    },
    {
      id: 6,
      name: "Wooden Doors",
      category: "doors",
      image: "Doors/wooden-doors.png",
    },
    {
      id: 7,
      name: "WPC Doors",
      category: "doors",
      image: "Doors/wpc.png",
      badge: "Comfort",
    },
    {
      id: 8,
      name: "Glass Handrails",
      category: "handrail",
      image: "Handrail/glass-handrail.png",
    },
    {
      id: 9,
      name: "stainless Steel Handrails",
      category: "handrail",
      image: "Handrail/stainles-steel-handrai.png",
      
    },
    {
      id: 10,
      name: "Bollards",
      category: "others",
      image: "others/bollards.png",
      
    },
    {
      id: 11,
      name: "Gates",
      category: "others",
      image: "others/gates.png",
      
    },
    {
      id: 12,
      name: "Hinges,Sliders & Essentials",
      category: "hardware",
      image: "hardware/doorHardwares.png",
      
    },
     {
      id: 13,
      name: "Door Handles",
      category: "hardware",
      image: "hardware/doorHandles.png",
      
    },
     {
      id: 14,
      name: "Staircase",
      category: "others",
      image: "others/staircase.png",
      
    },
  ];

  const filteredProducts =
    activeCategory === "all"
      ? products
      : products.filter((product) => product.category === activeCategory);

  return (
    <section
      id="products"
      className="relative py-16 overflow-hidden bg-gradient-to-br from-[#225a96] via-[#1a4670] to-[#0f2d47]"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-80 h-80 bg-gradient-to-br from-[#d02b37]/15 to-[#ff6b7a]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-tr from-[#225a96]/20 to-[#0f2d47]/15 rounded-full blur-2xl animate-float" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-[#1a4670]/20 via-transparent to-transparent rounded-full" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(245,245,245,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(245,245,245,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#d02b37]/20 to-[#ff6b7a]/15 rounded-full mb-4 backdrop-blur-sm border border-[#d02b37]/30"
          >
            <i className="ri-shopping-bag-line text-sm text-[#d02b37] mr-2" />
            <span
              className="text-[#f5f5f5] font-semibold text-xs tracking-wider uppercase"
              style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            >
              Our Products
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.4 }}
            className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
            Premium Collections
            {/* <span className="block bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] bg-clip-text text-transparent">
              Furniture Collection
            </span> */}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-sm md:text-base text-[#f5f5f5] max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
            Discover our curated selection of luxury office furniture designed
            to elevate your workspace and inspire productivity.
          </motion.p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {categories.map((category, index) => (
            <motion.button
              key={category.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
              whileHover={{
                scale: 1.05,
                backgroundColor:
                  activeCategory === category.id
                    ? "rgba(208, 43, 55, 0.3)"
                    : "rgba(34, 90, 150, 0.2)",
                boxShadow: "0 10px 20px rgba(34, 90, 150, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-500 flex items-center space-x-2 backdrop-blur-sm border-2 ${
                activeCategory === category.id
                  ? "bg-gradient-to-r from-[#d02b37]/20 to-[#ff6b7a]/15 text-white border-[#d02b37]/50"
                  : "bg-[#225a96]/10 text-[#f5f5f5] border-white/20 hover:text-white"
              }`}
              style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
            >
              <motion.i
                className={`${category.icon} text-xs`}
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              />
              <span className="text-xs">{category.name}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          layout
        >
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 60 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 + index * 0.2 }}
              // whileHover={{
              //   y: -8,
              //   scale: 1.02,
              //   boxShadow:
              //     "0 20px 40px rgba(208, 43, 55, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              // }}
              className="group relative  bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-[#d02b37]/40 transition-all duration-700 cursor-pointer"
              data-product-shop
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-[#d02b37]/20 via-[#d02b37]/10 to-transparent mix-blend-multiply pointer-events-none rounded-lg"></div>

              {/* Image */}
              <div className="relative overflow-hidden h-60">
                <motion.img
                  src={`/assets/products/${product.image}`}
                  alt={product.name}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                  whileHover={{ scale: 1.1 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#d02b37]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Content */}
              <div className="p-4">
                <motion.h3
                  className="text-base font-bold text-white mb-2 group-hover:text-[#d02b37] transition-colors duration-300 z-20"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  {product.name}
                </motion.h3>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 2.5 }}
          className="text-center mt-12"
        >
          <motion.button
            onClick={handleDownload}
            whileHover={{
              scale: 1.05,
              boxShadow:
                "0 20px 40px rgba(208, 43, 55, 0.4), 0 0 30px rgba(208, 43, 55, 0.3)",
            }}
            whileTap={{ scale: 0.95 }}
            className="relative overflow-hidden bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] hover:from-[#ff6b7a] hover:to-[#d02b37] text-white px-6 py-3 text-sm font-bold rounded-xl shadow-2xl transition-all duration-700 flex items-center space-x-2 mx-auto whitespace-nowrap"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700" />
            <span className="relative z-10">View Full Catalog</span>
            <motion.i
              className="ri-external-link-line text-sm relative z-10"
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
